import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { stripMarkdown } from './RowListEditors';
import AutoGrowingTextarea from './AutoGrowingTextarea';

// ─── Shared primitives ───────────────────────────────────────────────────────
const sectionLabel = { fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 5 };

const cleanInput = (readOnly) => ({
    fontSize: 13, fontWeight: 500, color: '#1E293B',
    border: 'none', borderBottom: `1px solid ${readOnly ? 'transparent' : '#E8EDF2'}`,
    outline: 'none', background: 'transparent',
    width: '100%', padding: '4px 0', boxSizing: 'border-box',
});

const cleanArea = (readOnly) => ({
    fontSize: 13, color: '#475569', lineHeight: 1.6,
    border: 'none', outline: 'none', background: 'transparent',
    width: '100%', padding: '3px 0', resize: 'none', boxSizing: 'border-box',
});

const addBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, width: '100%', padding: '9px 0',
    color: '#FF5526', background: 'transparent',
    border: '1.5px dashed #FFD0C2', borderRadius: 8,
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
    marginTop: 6, transition: 'all 0.15s',
};

const trashBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#CBD5E1', padding: 2, borderRadius: 4,
    display: 'flex', alignItems: 'center', lineHeight: 1,
    transition: 'color 0.15s',
    flexShrink: 0,
};

// ─── CenterTextListEditor (Cycle + Radial Hub) ───────────────────────────────
export const CenterTextListEditor = ({ value, onChange, readOnly }) => {
    const safe = value || { center_text: '', content: [''] };
    const items = Array.isArray(safe.content) ? safe.content : [''];

    const updateCenter = (v) => onChange({ ...safe, center_text: v });
    const updateItem = (idx, v) => { const next = [...items]; next[idx] = v; onChange({ ...safe, content: next }); };
    const addItem = () => onChange({ ...safe, content: [...items, ''] });
    const removeItem = (idx) => onChange({ ...safe, content: items.filter((_, i) => i !== idx) });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Center text */}
            <div style={{ background: '#F8F9FA', borderRadius: 10, padding: '12px 14px', border: '1px solid #E8EDF2' }}>
                <span style={sectionLabel}>Center Focus</span>
                <input
                    disabled={readOnly}
                    value={stripMarkdown(safe.center_text)}
                    onChange={e => updateCenter(e.target.value)}
                    placeholder="e.g., Quality Loop"
                    style={{ ...cleanInput(readOnly), fontWeight: 700, fontSize: 15, color: '#0F172A' }}
                />
            </div>

            {/* Surrounding items */}
            <div>
                <span style={sectionLabel}>Surrounding Items</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {items.map((item, idx) => (
                        <div
                            key={idx}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#fff', border: '1px solid #E8EDF2', borderRadius: 8 }}
                        >
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#CBD5E1', minWidth: 20, textAlign: 'center' }}>{idx + 1}</span>
                            <input
                                disabled={readOnly}
                                value={stripMarkdown(item)}
                                onChange={e => updateItem(idx, e.target.value)}
                                placeholder={`Item ${idx + 1}`}
                                style={{ ...cleanInput(readOnly), flex: 1, borderBottom: 'none' }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── MatrixEditor ─────────────────────────────────────────────────────────────
const QUADRANT_COLORS = {
    top_left: { bg: '#FFFFFF', border: '#E2E8F0', label: '↖ Top Left' },
    top_right: { bg: '#FFFFFF', border: '#E2E8F0', label: '↗ Top Right' },
    bottom_left: { bg: '#FFFFFF', border: '#E2E8F0', label: '↙ Bottom Left' },
    bottom_right: { bg: '#FFFFFF', border: '#E2E8F0', label: '↘ Bottom Right' },
};

export const MatrixEditor = ({ value, onChange, readOnly }) => {
    const safe = value || { center_text: '', content: { top_left: '', top_right: '', bottom_left: '', bottom_right: '' } };
    const updateQ = (k, v) => onChange({ ...safe, content: { ...safe.content, [k]: v } });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 14px', textAlign: 'center', background: '#FAFBFC' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Center</span>
                <input
                    disabled={readOnly}
                    value={stripMarkdown(safe.center_text)}
                    onChange={e => onChange({ ...safe, center_text: e.target.value })}
                    placeholder="e.g., SWOT Analysis"
                    style={{ display: 'block', width: '100%', fontWeight: 700, fontSize: 14, color: '#1E293B', background: 'transparent', border: 'none', outline: 'none', textAlign: 'center', marginTop: 2 }}
                />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {Object.entries(QUADRANT_COLORS).map(([key, { bg, border, label }]) => (
                    <div key={key} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px' }}>
                        <span style={{ ...sectionLabel, color: '#64748B' }}>{label}</span>
                        <textarea
                            disabled={readOnly}
                            value={stripMarkdown(safe.content?.[key] || '')}
                            onChange={e => updateQ(key, e.target.value)}
                            rows={3}
                            placeholder={`Content...`}
                            style={{ ...cleanArea(readOnly), fontSize: 12 }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── DetailedMatrixEditor ────────────────────────────────────────────────────
export const DetailedMatrixEditor = ({ value, onChange, readOnly }) => {
    const safe = value || {
        center_text: '',
        content: {
            top_left: { header: '', content: '' }, top_right: { header: '', content: '' },
            bottom_left: { header: '', content: '' }, bottom_right: { header: '', content: '' }
        }
    };
    const updateQ = (k, field, v) => onChange({ ...safe, content: { ...safe.content, [k]: { ...safe.content[k], [field]: v } } });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 14px', textAlign: 'center', background: '#FAFBFC' }}>
                <input
                    disabled={readOnly}
                    value={stripMarkdown(safe.center_text)}
                    onChange={e => onChange({ ...safe, center_text: e.target.value })}
                    placeholder="Center Text"
                    style={{ width: '100%', fontWeight: 700, fontSize: 14, color: '#1E293B', background: 'transparent', border: 'none', outline: 'none', textAlign: 'center' }}
                />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {Object.entries(QUADRANT_COLORS).map(([key, { bg, border, label }]) => (
                    <div key={key} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px' }}>
                        <span style={{ ...sectionLabel, color: '#64748B' }}>{label}</span>
                        <input
                            disabled={readOnly}
                            value={stripMarkdown(safe.content?.[key]?.header || '')}
                            onChange={e => updateQ(key, 'header', e.target.value)}
                            placeholder="Header"
                            style={{ ...cleanInput(readOnly), fontWeight: 600, marginBottom: 4 }}
                        />
                        <textarea
                            disabled={readOnly}
                            value={stripMarkdown(safe.content?.[key]?.content || '')}
                            onChange={e => updateQ(key, 'content', e.target.value)}
                            rows={2}
                            placeholder="Description"
                            style={{ ...cleanArea(readOnly), fontSize: 12 }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── BeforeAfterEditor ────────────────────────────────────────────────────────
export const BeforeAfterEditor = ({ value, onChange, readOnly }) => {
    const safe = value || { before: { header: '', content: [''] }, after: { header: '', content: [''] } };
    const updateSide = (side, field, val) => onChange({ ...safe, [side]: { ...safe[side], [field]: val } });
    const updateItem = (side, idx, val) => {
        const next = [...(safe[side].content || [])];
        next[idx] = val;
        onChange({ ...safe, [side]: { ...safe[side], content: next } });
    };
    const removeItem = (side, idx) => onChange({ ...safe, [side]: { ...safe[side], content: (safe[side].content || []).filter((_, i) => i !== idx) } });

    const sideConfig = {
        before: { bg: '#FFF1EC', border: '#FED7AA', color: '#9A3412', icon: '←', label: 'Before' },
        after: { bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D', icon: '→', label: 'After' },
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {Object.entries(sideConfig).map(([side, cfg]) => (
                <div key={side} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <span style={{ fontWeight: 800, color: cfg.color, fontSize: 16 }}>{cfg.icon}</span>
                        <input
                            disabled={readOnly}
                            value={safe[side]?.header || ''}
                            onChange={e => updateSide(side, 'header', e.target.value)}
                            placeholder={`${cfg.label} header`}
                            style={{ ...cleanInput(readOnly), fontWeight: 700, color: cfg.color }}
                        />
                    </div>
                    {(safe[side]?.content || []).map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                            <span style={{ fontSize: 11, color: cfg.color, fontWeight: 700, minWidth: 14 }}>•</span>
                            <input
                                disabled={readOnly}
                                value={stripMarkdown(item)}
                                onChange={e => updateItem(side, idx, e.target.value)}
                                placeholder={`Point ${idx + 1}`}
                                style={{ ...cleanInput(readOnly), flex: 1, borderBottom: 'none', fontSize: 12 }}
                            />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

// ─── BigNumberEditor ─────────────────────────────────────────────────────────
export const BigNumberEditor = ({ value, onChange, readOnly }) => {
    const safe = value || { summary: '', content: [{ number: '', label: '' }] };
    const updateItem = (idx, key, val) => {
        const next = safe.content.map((c, i) => i === idx ? { ...c, [key]: val } : c);
        onChange({ ...safe, content: next });
    };
    const addItem = () => onChange({ ...safe, content: [...safe.content, { number: '', label: '' }] });
    const removeItem = (idx) => onChange({ ...safe, content: safe.content.filter((_, i) => i !== idx) });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
                <span style={sectionLabel}>Summary</span>
                <AutoGrowingTextarea
                    disabled={readOnly}
                    value={stripMarkdown(safe.summary)}
                    onChange={e => onChange({ ...safe, summary: e.target.value })}
                    rows={2}
                    placeholder="e.g., VelocityRFP yields drastic efficiency improvements."
                    style={{ ...cleanArea(readOnly), border: '1px solid #E8EDF2', borderRadius: 8, padding: '8px 12px', background: '#FAFAFA' }}
                />
            </div>
            <div>
                <span style={sectionLabel}>Key Numbers</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {safe.content.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: '#fff', border: '1px solid #E8EDF2', borderRadius: 8 }}>
                            <input
                                disabled={readOnly}
                                value={item.number}
                                onChange={e => updateItem(idx, 'number', e.target.value)}
                                placeholder="10x"
                                style={{ ...cleanInput(readOnly), width: 60, fontWeight: 800, fontSize: 16, color: '#E9471D', textAlign: 'center', borderBottom: 'none' }}
                            />
                            <div style={{ width: 1, height: 28, background: '#E2E8F0', flexShrink: 0 }} />
                            <input
                                disabled={readOnly}
                                value={item.label}
                                onChange={e => updateItem(idx, 'label', e.target.value)}
                                placeholder="Label"
                                style={{ ...cleanInput(readOnly), flex: 1, borderBottom: 'none' }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── SectionDividerEditor ─────────────────────────────────────────────────────
export const SectionDividerEditor = ({ value, onChange, readOnly }) => {
    const safe = value || { headline: '' };
    return (
        <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '20px 18px', textAlign: 'center', border: '2px dashed #E2E8F0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>
                Section Divider Slide
            </div>
            <input
                disabled={readOnly}
                value={safe.headline}
                onChange={e => onChange({ ...safe, headline: e.target.value })}
                placeholder="e.g., EXECUTIVE SUMMARY"
                style={{
                    fontSize: 20, fontWeight: 800, color: '#1E293B',
                    background: 'transparent', border: 'none', outline: 'none',
                    textAlign: 'center', width: '100%', letterSpacing: '0.05em', textTransform: 'uppercase'
                }}
            />
            {readOnly && <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>This is a static section divider slide.</p>}
        </div>
    );
};

// ─── GenericJsonFormEditor ────────────────────────────────────────────────────
export const GenericJsonFormEditor = ({ value, onChange, readOnly }) => {
    const [jsonText, setJsonText] = React.useState('');
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        try { setJsonText(JSON.stringify(value, null, 2)); } catch { setJsonText(''); }
    }, [value]);

    const handleChange = (e) => {
        const txt = e.target.value;
        setJsonText(txt);
        try {
            const parsed = JSON.parse(txt);
            setError('');
            onChange(parsed);
        } catch {
            setError('Invalid JSON — fix before saving.');
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF1EC', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span style={{ fontSize: 12, color: '#9A3412' }}>Custom schema slide. Edit JSON safely below.</span>
            </div>
            <textarea
                disabled={readOnly}
                value={jsonText}
                onChange={handleChange}
                rows={12}
                spellCheck={false}
                style={{
                    width: '100%', fontFamily: 'monospace', fontSize: 12, padding: 12,
                    border: `1px solid ${error ? '#EF4444' : '#E2E8F0'}`, borderRadius: 8,
                    background: readOnly ? '#F8F9FA' : '#fff', outline: 'none', resize: 'vertical', boxSizing: 'border-box'
                }}
            />
            {error && <p style={{ color: '#EF4444', fontSize: 11, marginTop: 4 }}>{error}</p>}
        </div>
    );
};

// ─── ChartSidebarEditor ───────────────────────────────────────────────────────
export const ChartSidebarEditor = ({ value, onChange, readOnly }) => {
    const safe = value || {
        chart_heading: '',
        takeaway_heading: '',
        takeaway_content: '',
        chart_data: { categories: [], series: [] }
    };

    const updateField = (key, val) => {
        onChange({ ...safe, [key]: val });
    };

    const updateChartData = (newChartData) => {
        onChange({ ...safe, chart_data: newChartData });
    };

    const updateCategory = (idx, val) => {
        const nextCats = [...(safe.chart_data?.categories || [])];
        nextCats[idx] = val;
        updateChartData({ ...safe.chart_data, categories: nextCats });
    };

    const updateSeriesName = (sIdx, val) => {
        const nextSeries = (safe.chart_data?.series || []).map((s, i) => i === sIdx ? { ...s, name: val } : s);
        updateChartData({ ...safe.chart_data, series: nextSeries });
    };

    const updateSeriesValue = (sIdx, valIdx, val) => {
        const numericVal = parseFloat(val) || 0;
        const nextSeries = (safe.chart_data?.series || []).map((s, i) => {
            if (i === sIdx) {
                const nextVals = [...s.values];
                nextVals[valIdx] = numericVal;
                return { ...s, values: nextVals };
            }
            return s;
        });
        updateChartData({ ...safe.chart_data, series: nextSeries });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Takeaway and Headings section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: '#fff', border: '1px solid #E8EDF2', borderRadius: 10, padding: 14 }}>
                <span style={sectionLabel}>Takeaway & Chart Info</span>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', marginBottom: 3 }}>CHART HEADING</div>
                        <input
                            disabled={readOnly}
                            value={stripMarkdown(safe.chart_heading)}
                            onChange={e => updateField('chart_heading', e.target.value)}
                            placeholder="Chart Title"
                            style={{ ...cleanInput(readOnly) }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', marginBottom: 3 }}>TAKEAWAY HEADING</div>
                        <input
                            disabled={readOnly}
                            value={stripMarkdown(safe.takeaway_heading)}
                            onChange={e => updateField('takeaway_heading', e.target.value)}
                            placeholder="e.g., Key Takeaway"
                            style={{ ...cleanInput(readOnly) }}
                        />
                    </div>
                </div>
                <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', marginBottom: 3 }}>TAKEAWAY CONTENT</div>
                    <AutoGrowingTextarea
                        disabled={readOnly}
                        value={stripMarkdown(safe.takeaway_content)}
                        onChange={e => updateField('takeaway_content', e.target.value)}
                        placeholder="Detail the takeaways or insights shown in the chart..."
                        style={{ ...cleanArea(readOnly), minHeight: 40 }}
                    />
                </div>
            </div>

            {/* Chart Data matrix editor */}
            {safe.chart_data && (
                <div style={{ background: '#fff', border: '1px solid #E8EDF2', borderRadius: 10, padding: 14 }}>
                    <span style={sectionLabel}>Chart Data</span>
                    <div style={{ overflowX: 'auto', marginTop: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: '#F8F9FA', borderBottom: '1px solid #E2E8F0' }}>
                                    <th style={{ padding: '6px 8px', fontWeight: 600, color: '#475569', fontSize: 11 }}>SERIES NAME</th>
                                    {(safe.chart_data.categories || []).map((cat, cIdx) => (
                                        <th key={cIdx} style={{ padding: '6px 8px', fontWeight: 600, color: '#475569', fontSize: 11 }}>
                                            <input
                                                disabled={readOnly}
                                                value={cat}
                                                onChange={e => updateCategory(cIdx, e.target.value)}
                                                style={{ border: 'none', background: 'transparent', width: '80px', fontWeight: 600, color: '#475569', padding: 0 }}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(safe.chart_data.series || []).map((ser, sIdx) => (
                                    <tr key={sIdx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '6px 8px' }}>
                                            <input
                                                disabled={readOnly}
                                                value={ser.name}
                                                onChange={e => updateSeriesName(sIdx, e.target.value)}
                                                style={{ border: 'none', background: 'transparent', fontWeight: 600, color: '#1E293B', padding: 0 }}
                                            />
                                        </td>
                                        {(ser.values || []).map((val, vIdx) => (
                                            <td key={vIdx} style={{ padding: '6px 8px' }}>
                                                <input
                                                    disabled={readOnly}
                                                    type="number"
                                                    value={val}
                                                    onChange={e => updateSeriesValue(sIdx, vIdx, e.target.value)}
                                                    style={{ border: 'none', background: 'transparent', width: '50px', padding: 0 }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
